import { AppConfig } from '../core-bundle/config/app-config';
import { TIME_SPANS } from '@wetransfer/transfer/constant/times';
import PreviewHelper from '@wetransfer/transfer/helper/preview';
import Analytics from '@wetransfer/transfer/modules/analytics';
import I18n from '@wetransfer/transfer/modules/i18n';
import Store from '@wetransfer/transfer/store';
import { isPreviewable } from '@wetransfer/transfer/transfer-bundle/helpers/preview';

export default {
  /**
   * Determines whether a transfer can be downloaded
   * @param  {Object}  transfer the transfer object
   * @return {Boolean} Whether the transfer can be forwarded.
   */
  isDownloadingAllowed(transfer = {}) {
    if (!transfer.state) {
      return false;
    }

    return Boolean(transfer.state.match(/init|downloadable/));
  },

  /**
   * Determines whether a transfer can be previewed
   * @param  {Object}  transfer the transfer object
   * @return {Boolean} Whether the transfer can be forwarded.
   */
  isPreviewingAllowed(transfer = {}) {
    if (!transfer.state) {
      return false;
    }

    return Boolean(transfer.state.match(/init|downloadable/));
  },

  /**
   * Determines whether a transfer can be forwarded
   * @param  {Object}  transfer the transfer object
   * @return {Boolean} Whether the transfer can be forwarded.
   */
  isForwardingAllowed(transfer = {}) {
    if (!transfer.state) {
      return false;
    }

    if (transfer.paid === true) {
      return false;
    }

    return Boolean(transfer.state.match(/init|downloadable/));
  },

  /*
   * If the transfer in question is the same as the currentTransfer, then;
   * not allowed if the state of currentTransfer is anything but finished
   */
  isDeletionAllowed(transfer = {}, currentTransfer = {}) {
    if (transfer.id === currentTransfer.parent_id) {
      return false;
    }

    if (!transfer.state) {
      return false;
    }

    return Boolean(transfer.state.match(/init|downloadable/));
  },

  /**
   * Determines whether a transfer can be forwarded without the user having to confirm it
   * @return {Boolean} Whether the transfer can be forwarded without confirmation.
   */
  canForwardWithoutConfirmation() {
    const { transfer, channel } = Store.getState();

    if (transfer.state !== 'finished') {
      if (transfer.files.length || transfer.message.length) {
        return false;
      }

      if (
        transfer.recipients.length === 1 &&
        channel.defaultRecipient === transfer.recipients[0]
      ) {
        return true;
      }

      if (transfer.recipients.length) {
        return false;
      }
    }

    return true;
  },

  getRelativeExpirationDateLabel(seconds) {
    if (seconds === undefined) {
      return I18n.t('page.transfers.expiration_no_date');
    }

    const isLessThanADayOrDeletingNow =
      Math.round(seconds / TIME_SPANS.ONE_HOUR_IN_SECONDS) <= 24;

    if (isLessThanADayOrDeletingNow) {
      return I18n.t('page.transfers.expiration_next', {
        days: I18n.t('datetime.distance_in_words.x_hours', { count: 24 }),
      });
    }

    const isLessThanThreeDays =
      Math.round(seconds / TIME_SPANS.ONE_DAY_IN_SECONDS) <= 3;

    if (isLessThanThreeDays) {
      return I18n.t('page.transfers.expiration_next', {
        days: I18n.t('datetime.distance_in_words.x_days', { count: 3 }),
      });
    }

    const isLessThanSevenDays =
      Math.round(seconds / TIME_SPANS.ONE_DAY_IN_SECONDS) <= 7;

    if (isLessThanSevenDays) {
      return I18n.t('page.transfers.expiration_next', {
        days: I18n.t('datetime.distance_in_words.x_days', { count: 7 }),
      });
    }

    const isLessThanThirtyDays =
      Math.round(seconds / TIME_SPANS.ONE_DAY_IN_SECONDS) <= 30;

    if (isLessThanThirtyDays) {
      return I18n.t('page.transfers.expiration_next', {
        days: I18n.t('datetime.distance_in_words.x_days', { count: 30 }),
      });
    }

    const isLessThanSixMonths =
      Math.round(seconds / TIME_SPANS.DAYS_28_IN_SECONDS) <= 6;

    if (isLessThanSixMonths) {
      return I18n.t('page.transfers.expiration_next', {
        days: I18n.t('datetime.distance_in_words.x_months', { count: 6 }),
      });
    }

    const isLessThanTwelveMonths =
      Math.round(seconds / TIME_SPANS.DAYS_28_IN_SECONDS) <= 12;

    if (isLessThanTwelveMonths) {
      return I18n.t('page.transfers.expiration_next', {
        days: I18n.t('datetime.distance_in_words.x_months', { count: 12 }),
      });
    }

    return I18n.t('page.transfers.expiration_not_soon');
  },

  transferIsAboutToExpire(expiresAt) {
    if (expiresAt === null) {
      return false;
    }

    const diff = new Date(expiresAt).getTime() - new Date().getTime();

    return diff < AppConfig.transfer.showWarningThreshold;
  },

  getExpiryTimeInSeconds(expiryInSeconds, expiresAt) {
    if (expiryInSeconds) {
      return expiryInSeconds;
    }

    return (new Date(expiresAt).getTime() - new Date().getTime()) / 1000;
  },

  previewTransfer(transfer) {
    const {
      id: transferId,
      password_protected,
      security_hash: secret,
      items,
    } = transfer;

    const params = {
      transferId,
      secret,
    };

    const isTransferPreviewable = isPreviewable({ items, password_protected });

    const previewUrl =
      items.length === 1
        ? PreviewHelper.getPreviewUrl({ fileId: items[0].id, ...params })
        : PreviewHelper.getGridUrl(params);

    const url = isTransferPreviewable
      ? previewUrl
      : PreviewHelper.getTransferDownloadUrl(params);

    Analytics.trackSnowplowEvent({
      category: 'plus_account',
      action: 'click_preview',
      label: isTransferPreviewable
        ? 'transfer_detail_grid'
        : 'transfer_detail_download',
    });

    window.open(url);
  },
  normalizeExpiryDateOptions: (transferExpiryOptions) => {
    if (!transferExpiryOptions) {
      return {};
    }

    // The expiry options come in a random order from the backend.
    // We want to sort them from short to long duration, as this is
    // how we use them in the dropdown options.
    return transferExpiryOptions
      .sort((optionA, optionB) => {
        if (optionA === optionB) {
          return 0;
        }
        const numberOptionA = parseInt(optionA);
        const numberOptionB = parseInt(optionB);

        if (isNaN(numberOptionA)) {
          return 1;
        }

        if (isNaN(numberOptionB)) {
          return -1;
        }

        return numberOptionA - numberOptionB;
      })
      .reduce(
        (optionsObject, option) => ({
          ...optionsObject,
          [option]: I18n.t(`transfer.options.expire_in.${option}`),
        }),
        {}
      );
  },
};
